import { buildWorkflowSlugIndex } from '@Core/lib/tasks/utils/workflow';
import { componentRegistry } from '@Pages/pageRuntime/components/registry';
import { useSidebar } from '@Pages/pageRuntime/components/ui/useSidebar';
import { lazySdkDnd } from '@Pages/pageRuntime/dnd/lazyDnd';
import type { FileMetadataStore } from '@Pages/pageRuntime/fileMetadataStore';
import {
  useCalendarEvents,
  useCalendarSelection,
  useCalendarView,
  useCreateResourceRecordForm,
  useDataTable,
  useNewCalendarEvent,
  useResourceDataTable,
  useUpdateResourceRecordForm,
  useWorkflowForm,
  useWorkspaceFieldsForm
} from '@Pages/pageRuntime/hooks/lazyFormHooks';
import { useAccountingEntities } from '@Pages/pageRuntime/hooks/useAccountingEntities';
import { useAnalytics } from '@Pages/pageRuntime/hooks/useAnalytics';
import { useApi } from '@Pages/pageRuntime/hooks/useApi';
import { useAvailability } from '@Pages/pageRuntime/hooks/useAvailability';
import { useAvailableSlots } from '@Pages/pageRuntime/hooks/useAvailableSlots';
import {
  useSendBulkNotifications,
  useBulkNotificationRun
} from '@Pages/pageRuntime/hooks/useBulkNotifications';
import { useCalendarConnections } from '@Pages/pageRuntime/hooks/useCalendarConnections';
import { useCreateRecord } from '@Pages/pageRuntime/hooks/useCreateRecord';
import { useDeleteResourceRecord } from '@Pages/pageRuntime/hooks/useDeleteResourceRecord';
import { useEmailMessages } from '@Pages/pageRuntime/hooks/useEmailMessages';
import { useForm } from '@Pages/pageRuntime/hooks/useForm';
import { useInfiniteRecords } from '@Pages/pageRuntime/hooks/useInfiniteRecords';
import {
  useNotificationTypes,
  useNotificationTemplates
} from '@Pages/pageRuntime/hooks/useNotificationCatalog';
import { useNotificationConsent } from '@Pages/pageRuntime/hooks/useNotificationConsent';
import {
  useNotificationHistory,
  useNotificationHistoryDetail
} from '@Pages/pageRuntime/hooks/useNotificationHistory';
import { usePackageReleaseNotes } from '@Pages/pageRuntime/hooks/usePackageReleaseNotes';
import { usePayerBalances } from '@Pages/pageRuntime/hooks/usePayerBalances';
import { usePaymentRefundPreview } from '@Pages/pageRuntime/hooks/usePaymentRefundPreview';
import { usePoll } from '@Pages/pageRuntime/hooks/usePoll';
import { useQueryParams } from '@Pages/pageRuntime/hooks/useQueryParams';
import { useRecord } from '@Pages/pageRuntime/hooks/useRecord';
import { useRecords } from '@Pages/pageRuntime/hooks/useRecords';
import { useRecordScheduleTimelines } from '@Pages/pageRuntime/hooks/useRecordScheduleTimelines';
import { useRestoreResourceRecord } from '@Pages/pageRuntime/hooks/useRestoreResourceRecord';
import { useScheduleConflicts } from '@Pages/pageRuntime/hooks/useScheduleConflicts';
import {
  useUpcomingNotifications,
  useScheduleNotification,
  useCancelScheduledNotification,
  useHoldScheduledNotification,
  useResumeScheduledNotification,
  useScheduledNotification,
  useUpdateScheduledNotification,
  useSendScheduledNotificationNow
} from '@Pages/pageRuntime/hooks/useScheduledNotifications';
import { useSendEmail } from '@Pages/pageRuntime/hooks/useSendEmail';
import { useSendNotification } from '@Pages/pageRuntime/hooks/useSendNotification';
import { useSendResolvedNotification } from '@Pages/pageRuntime/hooks/useSendResolvedNotification';
import { useSharedFilters } from '@Pages/pageRuntime/hooks/useSharedFilters';
import { useStepper } from '@Pages/pageRuntime/hooks/useStepper';
import { useSubscriptionChangePreview } from '@Pages/pageRuntime/hooks/useSubscriptionChangePreview';
import { useSubscriptionPreview } from '@Pages/pageRuntime/hooks/useSubscriptionPreview';
import { useTheme } from '@Pages/pageRuntime/hooks/useTheme';
import { useTwoFactorBackupCodes } from '@Pages/pageRuntime/hooks/useTwoFactorBackupCodes';
import { useTwoFactorChallenge } from '@Pages/pageRuntime/hooks/useTwoFactorChallenge';
import { useTwoFactorSetup } from '@Pages/pageRuntime/hooks/useTwoFactorSetup';
import { useUpdateRecord } from '@Pages/pageRuntime/hooks/useUpdateRecord';
import { useWorkflowAction } from '@Pages/pageRuntime/hooks/useWorkflowAction';
import { useWorkspaceValues } from '@Pages/pageRuntime/hooks/useWorkspaceValues';
import { useLocalStorage } from '@reactuses/core';
import type { QueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';

import { createApi } from './api';
import { createAuth } from './auth';
import { createSdkPermissions } from './authorization';
import { createHelpers } from './helpers';
import { createPageHandle } from './pageHandle';
import type { PageSdkRequestTracker } from './requestActivity';
import { buildResourceHandles } from './resourceHandle';
import { createRouter, type RouterLike } from './router';
import type {
  CustomComponentRegistry,
  SdkContract,
  SdkAuthConfig,
  SdkNavigationMenuCatalog,
  SdkNavigationMenu,
  SdkResource,
  PageResourceData,
  PageRouteSettings,
  SdkHooks,
  SdkIdentity,
  SdkRouter,
  SdkUtils,
  SdkWorkspace,
  SdkWorkspaceField,
  SdkWorkspaceRecord,
  WorkflowRouteSettings
} from './types';
import { UnknownResourceError } from './UnknownResourceError';
import { createUtils } from './utils';

type BuildSdkArgs = {
  fileStore?: FileMetadataStore;
  /** The page's injected input data, read through `Sdk.helpers`. */
  data?: Record<string, unknown>;
  resources: Record<string, PageResourceData> | undefined;
  resourceIdsBySlug?: Record<string, string>;
  workspace: SdkWorkspaceRecord | null | undefined;
  /** Workspace field definitions, attached to `workspace.fields` keyed by field key. */
  workspaceFields?: SdkWorkspaceField[] | null;
  navigation: SdkNavigationMenuCatalog | null | undefined;
  identity: SdkIdentity | undefined;
  auth: SdkAuthConfig | null | undefined;
  customComponents: CustomComponentRegistry;
  axios: AxiosInstance;
  router: RouterLike;
  workflowRouteSettings: WorkflowRouteSettings;
  pageRouteSettings: PageRouteSettings;
  /**
   * The page's React Query client. Resource handle mutations invalidate its
   * caches on success so reactive hooks refresh automatically.
   */
  queryClient: QueryClient;
  /** Tracks imperative SDK requests that do not appear in React Query. */
  requestTracker?: PageSdkRequestTracker;
};

type BuildSdkCoreArgs = Omit<BuildSdkArgs, 'customComponents'>;

type SdkContractCore = Omit<SdkContract, 'Custom'>;

function unknownLookupMessage(thing: string, identifier: string, available: string[]): string {
  const list = available.length > 0 ? available.join(', ') : '(none registered)';
  return `Unknown ${thing} '${identifier}'. Available: ${list}`;
}

function resourceSlugIndex(resources: Record<string, PageResourceData>): Record<string, string> {
  const index = Object.create(null) as Record<string, string>;
  const ambiguous = new Set<string>();

  for (const [id, resource] of Object.entries(resources)) {
    const slug = resource.slug;

    if (!slug || ambiguous.has(slug)) {
      continue;
    }

    if (Object.hasOwn(resources, slug) && slug !== id) {
      continue;
    }

    if (Object.hasOwn(index, slug)) {
      delete index[slug];
      ambiguous.add(slug);
      continue;
    }

    index[slug] = id;
  }

  return index;
}

function resourceIdRegistry(idsBySlug: Record<string, string>): Record<string, string> {
  const registry = Object.create(null) as Record<string, string>;

  for (const [slug, id] of Object.entries(idsBySlug)) {
    if (slug && id) {
      registry[slug] = id;
    }
  }

  return registry;
}

function workspaceFieldCatalog(
  fields: SdkWorkspaceField[] | null | undefined
): Record<string, SdkWorkspaceField> {
  const catalog: Record<string, SdkWorkspaceField> = {};

  for (const field of fields ?? []) {
    if (field && typeof field.key === 'string' && field.key !== '') {
      catalog[field.key] = field;
    }
  }

  return catalog;
}

export function buildSdkCore(args: BuildSdkCoreArgs): SdkContractCore {
  const resources = args.resources ?? {};
  const workspace: SdkWorkspace = args.workspace
    ? { ...args.workspace, fields: workspaceFieldCatalog(args.workspaceFields) }
    : null;
  const workspaceValues = workspace?.values ?? { default_timezone: 'UTC' };
  const navigation = args.navigation ?? {};
  const identity = args.identity ?? null;

  const api = createApi(args.axios, args.requestTracker);
  const router: SdkRouter = createRouter(args.router);
  const auth = createAuth(router, args.auth);

  const resourceHandles = buildResourceHandles(resources, api, args.queryClient, args.fileStore);
  const resourceSlugs = resourceSlugIndex(resources);
  const workflowSlugs = buildWorkflowSlugIndex(args.workflowRouteSettings);
  const resourceIdsBySlug = resourceIdRegistry(args.resourceIdsBySlug ?? {});

  const resourceOrNull = (id: string): SdkResource | null => {
    const resourceId = Object.hasOwn(resourceHandles, id) ? id : resourceSlugs[id];

    return resourceId && Object.hasOwn(resourceHandles, resourceId)
      ? resourceHandles[resourceId]
      : null;
  };

  const utils: SdkUtils = createUtils({
    workspaceFields: workspaceValues,
    workflowRouteSettings: args.workflowRouteSettings,
    workflowSlugIndex: workflowSlugs,
    pageRouteSettings: args.pageRouteSettings,
    router
  });

  return {
    Components: componentRegistry,
    Dnd: lazySdkDnd,

    resource: (id: string): SdkResource => {
      const handle = resourceOrNull(id);
      if (!handle) {
        throw new UnknownResourceError(
          unknownLookupMessage('resource', id, [
            ...Object.keys(resourceSlugs),
            ...Object.keys(resourceHandles)
          ])
        );
      }
      return handle;
    },
    resourceOrNull,
    resourceId: (resourceSlug: string): string => {
      const resourceId = resourceIdsBySlug[resourceSlug];

      if (!resourceId) {
        throw new UnknownResourceError(
          unknownLookupMessage('resource slug', resourceSlug, Object.keys(resourceIdsBySlug))
        );
      }

      return resourceId;
    },
    resourceIds: () => Object.keys(resourceHandles),

    page: (id: string) => createPageHandle(id, utils.page, args.pageRouteSettings),

    workspace,

    navigationMenu: (id: string): SdkNavigationMenu => {
      const menu = (navigation as Record<string, SdkNavigationMenu | undefined>)[id];
      if (!menu) {
        // Unknown navigation-menu lookups stay a plain Error so they keep
        // reporting to remote telemetry.
        throw new Error(unknownLookupMessage('navigation menu', id, Object.keys(navigation)));
      }
      return menu;
    },
    navigationMenuIds: () => Object.keys(navigation),

    hooks: {
      useAnalytics,
      useApi,
      useSubscriptionChangePreview,
      useSubscriptionPreview,
      useAvailableSlots,
      useAvailability,
      useScheduleConflicts,
      usePaymentRefundPreview,
      usePayerBalances,
      usePackageReleaseNotes,
      useAccountingEntities,
      usePoll,
      useRecord,
      useRecords,
      useSharedFilters,
      useInfiniteRecords,
      useQueryParams,
      useCreateRecord,
      useUpdateRecord,
      useDeleteResourceRecord,
      useRestoreResourceRecord,
      useSendEmail,
      useNotificationTypes,
      useNotificationTemplates,
      useSendNotification,
      useNotificationConsent,
      useCalendarConnections,
      useTwoFactorChallenge,
      useTwoFactorSetup,
      useTwoFactorBackupCodes,
      useUpcomingNotifications,
      useScheduleNotification,
      useCancelScheduledNotification,
      useHoldScheduledNotification,
      useResumeScheduledNotification,
      useScheduledNotification,
      useUpdateScheduledNotification,
      useSendScheduledNotificationNow,
      useEmailMessages,
      useNotificationHistory,
      useNotificationHistoryDetail,
      useSendBulkNotifications,
      useBulkNotificationRun,
      useSendResolvedNotification,
      useCreateResourceRecordForm,
      useUpdateResourceRecordForm,
      useWorkflowForm,
      useWorkspaceFieldsForm,
      useWorkflowAction,
      useRecordScheduleTimelines,
      useDataTable,
      useResourceDataTable,
      useCalendarView,
      useCalendarEvents,
      useNewCalendarEvent,
      useCalendarSelection,
      useWorkspaceValues,
      useSidebar,
      useStepper,
      useLocalStorage,
      useTheme
    } satisfies SdkHooks,

    helpers: createHelpers(args.data ?? {}),

    useForm,
    useCalendarView,
    useCalendarEvents,
    useNewCalendarEvent,
    router,

    api,

    identity,

    auth,

    permissions: createSdkPermissions(identity),

    utils
  };
}

export function withCustomComponents(
  core: SdkContractCore,
  customComponents: CustomComponentRegistry
): SdkContract {
  return {
    ...core,
    Custom: customComponents
  };
}

export function buildSdk(args: BuildSdkArgs): SdkContract {
  const { customComponents, ...coreArgs } = args;

  return withCustomComponents(buildSdkCore(coreArgs), customComponents);
}
